import { DayKey, DefaultShiftTimes, DefaultShiftTimesString } from '@/services/settings/systemSettings.types';
import { differenceInCalendarYears, differenceInDays, differenceInMinutes, format, minutesToSeconds } from 'date-fns';
import strings from '../pages/shifts/shifts.translations';

export const timeStringToDate = (timeString: string) => {
  const splitString = timeString.split(':');
  const hours = parseInt(splitString[0]);
  const minutes = parseInt(splitString[1]);

  const date = new Date(0);
  date.setHours(hours);
  date.setMinutes(minutes);

  return date;
};

export const dateToTimeString = (date: Date) => {
  return format(date, 'HH:mm');
};

export const dateToSecondsSinceMidnight = (date: Date) => {
  return date.getHours() * 60 * 60 + date.getMinutes() * 60 + date.getSeconds();
};

export const secondsSinceMidnightToTimeString = (seconds: number) => {
  return dateToTimeString(secondsSinceMidnightToDate(seconds));
};

export const secondsSinceMidnightToDate = (secondsSinceMidnight: number) => {
  const date = new Date(0);

  const hours = Math.floor(secondsSinceMidnight / 3600);
  const secondsSinceHour = secondsSinceMidnight - hours * 3600;
  const minutes = Math.floor(secondsSinceHour / 60);
  const secondsSinceMinute = secondsSinceHour - minutes * 60;

  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(secondsSinceMinute);

  return date;
};

export const timeStringToSecondsSinceMidnight = (timeString: string) => {
  const splitString = timeString.split(':');

  const hours = parseInt(splitString[0]);
  const minutes = parseInt(splitString[1]);
  return hoursToSeconds(hours) + minutesToSeconds(minutes);
};

export const calculateMinutesDuration = (start: Date, end: Date) => {
  return differenceInMinutes(Math.floor(stripSeconds(end)), Math.floor(stripSeconds(start)));
};

export const displayMinutesAsHoursMinutes = (minutes: number) => {
  const numberOfHours = Math.floor(minutes / 60);
  const numberOfMinutes = minutes - numberOfHours * 60;

  let stringToReturn = '';

  if (numberOfHours > 0) {
    stringToReturn += `${numberOfHours}${strings.common.hourShortHand} `;
  }

  stringToReturn += `${numberOfMinutes}${strings.common.minuteShortHand}`;

  return stringToReturn;
};

export const secondsToDate = (x: number) => {
  const date = new Date(0);
  date.setHours(Math.floor(x / 3600), (x % 3600) / 60);
  return date;
};

export const secondsToTimeString = (seconds: number) => {
  const hoursCount = Math.floor(seconds / (60 * 60));
  let hours = hoursCount.toString();
  if (hours.length === 1) {
    hours = '0' + hours;
  }

  const minutesCount = Math.floor((seconds % (60 * 60)) / 60);
  let minutes = minutesCount.toString();
  if (minutes.length === 1) {
    minutes = '0' + minutes;
  }

  return hours + ':' + minutes;
};

export const secondsToHoursAndMinutesString = (seconds: number) => {
  const hoursCount = Math.floor(seconds / (60 * 60));
  const hours = hoursCount.toString();

  const minutesCount = Math.floor((seconds % (60 * 60)) / 60);
  let minutes = minutesCount.toString();
  if (minutes.length === 1) {
    minutes = '0' + minutes;
  }

  return `${hours}${strings.common.hourShortHand} ${minutes}${strings.common.minuteShortHand}`;
};

export const hoursToTimeString = (hours: number) => {
  return secondsToTimeString(hours * 60 * 60);
};

export const secondsToDisplayHours = (seconds: number) => {
  return Math.round(secondsToHours(seconds) * 10) / 10;
};

export const secondsToHours = (seconds: number) => {
  return seconds / 60 / 60;
};

export const hoursToSeconds = (hours: number) => {
  return hours * 60 * 60;
};

export const isDateInPast = (dateOne: Date, dateTwo: Date) =>
  new Date(dateOne).setHours(0, 0, 0, 0) < new Date(dateTwo).setHours(0, 0, 0, 0);

export const convertDateToGQLFormat = (date: Date) => format(date, 'yyyy-MM-dd');

export const convertToUtcDate = (iso8601string?: string) => {
  if (iso8601string) {
    if (iso8601string.indexOf('+') > -1) {
      // timezone is already included, we don't handle this case for now, but have to avoid runtime error.
      return new Date(iso8601string);
    }
    // This is a UTC string. Add 'Z' if the string doesn't end with a 'Z'.
    return iso8601string.endsWith('Z') ? new Date(iso8601string) : new Date(iso8601string + 'Z');
  }
  return undefined;
};

export const createUTCDate = (year: number, month: number, day: number) => {
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
};

export const formatUTCDate = (date: Date) => {
  return createUTCDate(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getDaysInMonth = (month: number, year: number): Date[] => {
  const lastOfMonth = new Date(year, month + 1, 0);
  const monthLength = lastOfMonth.getDate();

  return Array.from({ length: monthLength }, (_, i) => createUTCDate(year, month, i + 1));
};

export const getDatePeriod = (startDate: Date, endDate: Date): Date[] => {
  const dates: Date[] = [];

  const dateToAdd = new Date(startDate);
  while (dateToAdd <= endDate) {
    dates.push(formatUTCDate(dateToAdd));
    dateToAdd.setDate(dateToAdd.getDate() + 1);
  }

  return dates;
};

export const isDateEqual = (dateOne: Date, dateTwo: Date) => {
  return (
    dateOne.getFullYear() === dateTwo.getFullYear() &&
    dateOne.getMonth() === dateTwo.getMonth() &&
    dateOne.getDate() === dateTwo.getDate()
  );
};

export const getStartAndEndDateOfMonth = (date: Date) => {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return {
    startDate: firstDay,
    endDate: lastDay
  };
};

export const dateToApiFormat = (date: Date) => {
  const utcDate = formatUTCDate(date);

  return utcDate.toISOString().substring(0, 10);
};

export const dateToGQLFormat = (date: Date) => {
  return date.toISOString();
};

export const dateStringToApiFormat = (dateString: string) => {
  return dateString.substring(0, 10);
};

export const stripSeconds = (time: Date) => {
  return new Date(time).setSeconds(0, 0);
};

export const compareDateToRosterDate = (date: Date, rosterDate: string) => {
  return rosterDate.substring(0, 19) === date.toISOString().substring(0, 19);
};

export const dayDiff = (startDate: number | Date, endDate: number | Date) => {
  return Math.abs(differenceInDays(startDate, endDate));
};

export const getDaysInWeek = (startDate: Date): Date[] => {
  return Array.from({ length: 7 }, (_, i) =>
    createUTCDate(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate() + i)
  );
};

export const getLastMonthDay = (dateToReverse: Date, monthDay: number) => {
  const date = new Date(dateToReverse);
  date.setUTCHours(0, 0, 0, 0);

  let dateFound = false;
  // Walk backwards until we find the right day, or a close enough approximation.
  while (!dateFound) {
    // First, we'll be walking back to the start of the month.
    // If we happen to hit the right day, then we're done.
    if (date.getUTCDate() === monthDay) {
      dateFound = true;
    }

    if (date.getUTCMonth() !== dateToReverse.getUTCMonth()) {
      // We've stepped into the previous month.
      // We need to check if we've already passed the month day.
      // (eg. We were looking for a 31st, and stepped into February)
      if (date.getUTCDate() < monthDay) {
        dateFound = true;
      }
    }

    if (dateFound) {
      break;
    } else {
      date.setUTCDate(date.getUTCDate() - 1);
    }
  }

  return date;
};

export const getNextMonthDay = (dateToAdvance: Date, monthDay: number) => {
  const date = new Date(dateToAdvance);
  date.setUTCHours(0, 0, 0, 0);

  let dateFound = false;
  let previousDayChecked: Date | undefined = undefined;
  // Walk forwards until we find the right day, or a close enough approximation.
  while (!dateFound) {
    // First, we'll be walking ahead to the end of the month.
    // If we happen to hit the right day, then we're done.
    if (date.getUTCDate() === monthDay) {
      dateFound = true;
    }

    if (previousDayChecked && date.getUTCMonth() !== dateToAdvance.getUTCMonth() && date.getUTCDate() === 1) {
      // We've stepped into the next month.
      // We need to check if we passed the month day
      // (eg. We were looking for a 31st, and stepped into March)
      if (monthDay === 0 || previousDayChecked.getUTCDate() < monthDay) {
        dateFound = true;

        // Because we missed the month day at the end of the last month,
        // take the last day of the month instead.
        date.setUTCDate(date.getUTCDate() - 1);
      }
    }

    if (dateFound) {
      break;
    } else {
      previousDayChecked = new Date(date);
      date.setUTCDate(date.getUTCDate() + 1);
    }
  }

  return date;
};

export const yearDiff = (startDate: number | Date, endDate: number | Date) => {
  return Math.abs(differenceInCalendarYears(startDate, endDate));
};

export const isEmployeeUnderage = (dob: string) => {
  return yearDiff(new Date(), new Date(dob)) < 18;
};

export const printTimePeriod = (startTimeInSeconds: number, endTimeInSeconds: number) => {
  return secondsToTimeString(startTimeInSeconds) + ' - ' + secondsToTimeString(endTimeInSeconds);
};

export const getDaysInThreeMonths = (selectedDate: Date) => {
  const prev = new Date(selectedDate);
  prev.setUTCDate(0);

  const next = new Date(selectedDate);
  next.setUTCDate(32);

  const prevMonth = getDaysInMonth(prev.getUTCMonth(), prev.getUTCFullYear());
  const currMonth = getDaysInMonth(selectedDate.getUTCMonth(), selectedDate.getUTCFullYear());
  const nextMonth = getDaysInMonth(next.getUTCMonth(), next.getUTCFullYear());

  return [...prevMonth, ...currMonth, ...nextMonth];
};

export const getMonthBounds = (date: Date): [Date, Date] => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();

  const start = new Date(Date.UTC(year, month, 1));
  const end = new Date(Date.UTC(year, month + 1, 0));

  return [start, end];
};

export const printDateMonthAndYear = (date: Date) => {
  return format(date, 'MMMM yyyy');
};

export const printDateMonth = (date: Date) => {
  return format(date, 'MMMM');
};

export const getDayKey = (date: Date): DayKey => {
  return format(date, 'EEEE').toLowerCase() as DayKey;
};

export const shiftTimesToStrings = (times: DefaultShiftTimes): DefaultShiftTimesString =>
  Object.fromEntries(
    Object.entries(times).map(([day, { start, end }]) => [
      day,
      {
        start: secondsSinceMidnightToTimeString(start),
        end: secondsSinceMidnightToTimeString(end)
      }
    ])
  ) as DefaultShiftTimesString;

export const shiftStringsToSeconds = (times: DefaultShiftTimesString): DefaultShiftTimes =>
  Object.fromEntries(
    Object.entries(times).map(([day, { start, end }]) => [
      day,
      {
        start: timeStringToSecondsSinceMidnight(start),
        end: timeStringToSecondsSinceMidnight(end)
      }
    ])
  ) as DefaultShiftTimes;

const normaliseValue = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (value.includes(':')) return timeStringToSecondsSinceMidnight(value);
    const parsed = Number(value);
    if (!isNaN(parsed)) return parsed;
  }
  return 0;
};

export const normaliseToShiftTimesSeconds = (times: DefaultShiftTimes | DefaultShiftTimesString): DefaultShiftTimes =>
  Object.fromEntries(
    Object.entries(times).map(([day, { start, end }]) => [
      day,
      {
        start: normaliseValue(start),
        end: normaliseValue(end)
      }
    ])
  ) as DefaultShiftTimes;
